<script setup lang="ts">
import { getBaseSiteUrl } from '~/composables/useSiteUrl'

export type BreadCrumbsProps = {
  path?: string // The path to generate breadcrumbs from
  links?: { text: string; url: string }[] // Or provide an array of links
  label?: string
  disabled?: boolean
  isCurrent?: boolean
}

const props = withDefaults(defineProps<BreadCrumbsProps>(), {
  label: 'breadcrumbs',
  path: '',
  isCurrent: true,
})

const { mhjEnvironment } = useSiteUrl()
const { t, locale } = useI18n()

// prettier-ignore
const PATH_I18N_MAP = {
  'my-account':         'my_account.side_menu.header.my_account',
  'account-activation': 'my_account.side_menu.header.account_activation',
  'order-history':      'my_account.side_menu.links.order_history',
  'contact-details':    'my_account.side_menu.links.contact_details',
  'privacy-policy':     'my_account.side_menu.links.privacy_policy',
  'wishlist':           'my_account.side_menu.links.wishlist',
  'my-offers':          'my_account.side_menu.links.my_offers',
  'customer-service':   'customer_service.breadcrumb',
  'contact-us':         'customer_service.contact_us.breadcrumb',
  'stores':             'store_page.breadcrumb',
} as Record<string, string>

function slugToBreadcrumbLinks(slug: string) {
  const localContext = {
    i18n: { locale: localeConfig[locale.value].code },
    env: { mhjEnvironment },
  }
  const baseSiteUrl = getBaseSiteUrl(localContext) || ''
  const origin = baseSiteUrl.replace(/\/$/, '') // Remove trailing slash
  const parts = slug.replace(/^\/|fr\//g, '').split('/') // Remove leading slash and /fr/ and split
  const links = parts.map((part, index) => {
    return {
      url: `${origin}/${parts.slice(0, index + 1).join('/')}`,
      text: PATH_I18N_MAP[part] ? t(PATH_I18N_MAP[part]) : part.replaceAll('-', ' '),
    }
  })
  const home = { text: t('home_text'), url: `${origin}/` }
  return [home, ...links]
}

const breadcrumbLinks = ref(props.links || slugToBreadcrumbLinks(props.path))

watch(
  () => props.path,
  (newPath) => {
    breadcrumbLinks.value = slugToBreadcrumbLinks(newPath)
  },
  { immediate: true },
)
watch(
  () => props.links,
  (newLinks) => {
    breadcrumbLinks.value = newLinks || slugToBreadcrumbLinks(props.path)
  },
  { immediate: true },
)

function getItemListData() {
  return breadcrumbLinks?.value.map((link, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      name: link.text,
      item: link.url,
    }
  })
}

function structuredData() {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: getItemListData(),
  })
}

useHead(() => {
  return {
    script: [
      {
        key: 'breadcrumb-schema',
        type: 'application/ld+json',
        innerHTML: structuredData(),
      },
    ],
  }
})
</script>

<template>
  <nav :aria-label="label" class="breadcrumbs">
    <ol>
      <li v-for="(link, index) in breadcrumbLinks" :key="index" class="breadcrumbs__item">
        <span v-if="index > 0 && link.text" aria-hidden="true" class="breadcrumbs__divider">/</span>
        <Anchor
          v-if="index === breadcrumbLinks.length - 1 && !disabled"
          aria-current="location"
          :url="link.url"
          class="breadcrumbs__link"
          :class="{ 'breadcrumbs__link--current': isCurrent }"
        >
          {{ link.text }}
        </Anchor>
        <span
          v-else-if="index === breadcrumbLinks.length - 1 && disabled"
          aria-current="location"
          class="breadcrumbs__link--current"
        >
          {{ link.text }}
        </span>
        <Anchor v-else :url="link.url" class="breadcrumbs__link">
          {{ link.text }}
        </Anchor>
      </li>
    </ol>
  </nav>
</template>

<style lang="postcss">
.breadcrumbs {
  overflow: hidden;
  font-size: var(--text-xsmall);

  ol {
    margin-block: var(--space-50);
    padding: 0;
  }

  .breadcrumbs__item {
    text-transform: capitalize;
    display: inline-flex;
  }
  .breadcrumbs__divider {
    margin-inline: var(--space-30);
  }

  .breadcrumbs__link {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .breadcrumbs__link--current {
    color: var(--text-secondary);
  }

  .breadcrumbs__item:nth-child(n + 5) {
    display: none;

    @media (--desktop) {
      display: inline-flex;
    }
  }
}
</style>
